/**
 * useAddEmail Hook
 *
 * Adds email to account and sends verification code using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const AddEmailDocument = `
  mutation AddEmailToAccount($email: String!) {
    addEmailToAccount(email: $email) {
      result
      mergeRequest { id status }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

type AddEmailResult = "SENT" | "MERGE_REQUIRED" | "ALREADY_LINKED";

interface AddEmailResponse {
  result: AddEmailResult;
  mergeRequest: { id: string; status: string } | null;
}

interface UseAddEmailResult {
  addEmail: (email: string) => Promise<AddEmailResponse>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Add email to account and send verification code.
 */
export function useAddEmail(): UseAddEmailResult {
  const [result, executeMutation] = useMutation<
    { addEmailToAccount: AddEmailResponse },
    { email: string }
  >(AddEmailDocument);

  const addEmail = useCallback(
    async (email: string): Promise<AddEmailResponse> => {
      try {
        const response = await executeMutation({
          email: email.toLowerCase().trim(),
        });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to add email"
          );
        }

        if (!response.data?.addEmailToAccount) {
          throw new Error("No response from server");
        }

        errorTracking.addBreadcrumb({
          category: "profile",
          message: "Email added",
          data: { result: response.data.addEmailToAccount.result },
        });

        return response.data.addEmailToAccount;
      } catch (err) {
        const emailError =
          err instanceof Error ? err : new Error("Failed to add email");

        errorTracking.captureError(emailError, {
          tags: { feature: "profile", action: "addEmail" },
        });

        throw emailError;
      }
    },
    [executeMutation]
  );

  return {
    addEmail,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
